
import Vue from "vue";
import Location from "@/components/Tables/Location/Location.vue";
import AssociatedLocationTable from "@/components/Tables/Location/Location.vue";
import TruncatedTextCell from "@/components/shared/TruncatedTextCell";
import EntityIdLink from "@/components/shared/EntityIdLink";
import BlocksFavourites from "@/components/shared/BlocksFavourites";
import PostedJobs from "@/components/PostedJobs";
import { format } from "date-fns";
import {
  getNotesByLocation,
  getFeedbackByLocation,
  blockTalentFromLocation,
  reactivateLocation,
  totalItemsByTable,
  createNote,
  updateTable,
  uploadOfficeDocumentToS3,
  getLocationStats,
  getLocationInvoicesPaginated,
  getLocationPaymentsPaginated,
  getLocationPlacementFeesPaginated,
  getLocationCancellationsPaginated,
  updateLocationCancellation,
  updateLocationLoginEmail,
  getLocationTalentReviewsPaginated,
  getLocationSiblings,
  updateInvoice
} from "@/api";
import type { LocationStats } from "@/api";
import { constants } from "@/utils/constants";
import {
  createCollapsedLocationSections,
  nextLocationSectionState
} from "./locationCollapsedSections";
import { buildOfficePaidUpdate } from "@/helpers/paymentLifecycleAdminGuard";

const OFFICE_CANCELLATION_REASON_OPTIONS = [
  { value: "permanentStaffCovered", text: "Permanent Staff Covered" },
  { value: "patientsCancelled", text: "Patients Cancelled" },
  { value: "doubleBooked", text: "Double-booked with another temp" },
  { value: "badExperiende", text: "Previous bad experience" },
  { value: "tempNoShowed", text: "Temp No-Showed" },
  { value: "other", text: "Other" },
  { value: "onBehalfOfTalent", text: "Talent asked the office to cancel on their behalf" }
];
const OFFICE_CANCELLATION_REASON_CODES = new Set(
  OFFICE_CANCELLATION_REASON_OPTIONS.map(option => option.value)
);

export default Vue.extend({
  name: "DentalOfficesMasterDetail",
  components: {
    Location,
    AssociatedLocationTable,
    TruncatedTextCell,
    EntityIdLink,
    BlocksFavourites,
    PostedJobs
  },
  data() {
    return {
      selectedLocation: null as any,
      collapsedSections: createCollapsedLocationSections() as Record<
        string,
        boolean
      >,
      associatedLocations: [] as any[],
      associatedLocationsError: "" as string,
      hygienistIdToBlock: null as number | null,
      blockTalentDialog: false,
      reactivateDialog: false,
      reactivateAccountDialog: false,
      totalLocationCount: 0,
      addNoteDialog: false,
      newNote: "",
      officeDocumentFile: null as File | null,
      officeDocumentError: "" as string,
      officeDocumentRules: [
        (v: File | null) => !!v || "File is required",
        (v: File | null) => {
          if (!v) return true;
          const isPdf =
            v.type === "application/pdf" ||
            v.name.toLowerCase().endsWith(".pdf");
          return isPdf || "Only PDF files are allowed";
        }
      ] as Array<(v: File | null) => boolean | string>,

      // T2-6866: inline notification-email edit (Location.email only)
      editingNotificationEmail: false,
      notificationEmailDraft: "",
      notificationEmailError: "",
      savingNotificationEmail: false,

      // T2-6223: inline login-email edit
      editingLoginEmail: false,
      loginEmailDraft: "",
      loginEmailError: "",
      savingLoginEmail: false,

      // Data arrays (jobs moved into the PostedJobs component, T2-6224)
      invoicesData: [] as any[],
      paymentsData: [] as any[],
      placementFeesData: [] as any[],
      cancellationsData: [] as any[],
      notesData: [] as any[],
      feedbackData: [] as any[],

      // Server-side totals
      invoicesTotal: 0,
      paymentsTotal: 0,
      placementFeesTotal: 0,
      cancellationsTotal: 0,

      // Pagination/sort options per section
      invoicesOptions: { page: 1, itemsPerPage: 5, sortBy: ["id"], sortDesc: [true] } as any,
      paymentsOptions: { page: 1, itemsPerPage: 5, sortBy: ["id"], sortDesc: [true] } as any,
      placementFeesOptions: { page: 1, itemsPerPage: 10, sortBy: ["startDate"], sortDesc: [true] } as any,
      cancellationsOptions: { page: 1, itemsPerPage: 10, sortBy: ["cancelTimestamp"], sortDesc: [true] } as any,
      placementFeesHeaders: [
        { text: "Job ID", value: "jobId", width: "80px", sortable: false },
        { text: "Shift Date", value: "startDate", width: "110px", sortable: false },
        { text: "PF Amount", value: "amountLabel", sortable: false },
        { text: "Status", value: "statusLabel", width: "110px", sortable: false },
        { text: "Timestamp", value: "timestamp", width: "150px", sortable: false }
      ],
      cancellationsHeaders: [
        { text: "ID", value: "id", width: "70px" },
        { text: "Job ID", value: "jobId", width: "80px", sortable: false },
        { text: "Shift Date", value: "shiftDate", width: "110px" },
        { text: "Cancelled", value: "cancelTimestamp", width: "150px" },
        { text: "Office SNC", value: "shortNotice", width: "90px", sortable: false },
        { text: "Talent", value: "hygienistId", width: "120px", sortable: false },
        { text: "Reason", value: "reason", sortable: false },
        { text: "Details", value: "details", sortable: false },
        { text: "", value: "actions", width: "90px", sortable: false }
      ],

      // AbortControllers for cancelling in-flight requests
      abortControllers: {
        jobs: null as AbortController | null,
        invoices: null as AbortController | null,
        payments: null as AbortController | null,
        cancellations: null as AbortController | null
      },

      // Loading states
      loading: {
        jobs: false, 
        invoices: false,
        payments: false,
        placementFees: false,
        cancellations: false,
        notes: false,
        feedback: false,
        blockTalent: false,
        reactivate: false,
        reactivateAccount: false,
        addNote: false,
        officeDocumentUpload: false,
        associatedLocations: false
      },

      // Table headers
      invoicesHeaders: [
        { text: "ID", value: "id", width: "60px" },
        { text: "Job", value: "jobId", width: "60px" },
        { text: "Amount", value: "totalInvoiceAmt", width: "90px" },
        { text: "Date", value: "createdOn", width: "100px" },
        { text: "Office Marked Paid", value: "dentistMarkedPaid", width: "130px", sortable: false },
        { text: "Talent Marked Paid", value: "hygienistMarkedPaid", width: "130px", sortable: false },
        { text: "Expected Payment Method", value: "paymentMethodLabel", width: "180px", sortable: false }
      ],
      paymentsHeaders: [
        { text: "ID", value: "id", width: "60px" },
        { text: "Job", value: "jobId", width: "60px" },
        { text: "Amount", value: "amount", width: "90px" },
        { text: "Date", value: "date", width: "100px" }
      ],
      notesHeaders: [
        { text: "Note", value: "note" },
        { text: "Source", value: "source", width: "100px" },
        { text: "Created", value: "createdOn", width: "120px" }
      ],
      
      // Show Stats Dialog
      showStatsDialog: false,
      loadingStats: false,
      locationStats: null as LocationStats | null,

      // Show Talent Reviews Dialog
      showTalentReviewsDialog: false,
      loadingTalentReviews: false,
      loadingTalentReviewsTable: false,
      talentReviewsData: [] as any[],
      talentReviewsTotal: 0,
      talentReviewsOptions: { page: 1, itemsPerPage: 5, sortBy: ["reviewDate"], sortDesc: [true] } as any,
      talentReviewsSearch: "",
      talentReviewsAbortController: null as AbortController | null,
      feedbackTextDialog: false,
      feedbackTextDialogContent: "",
      talentReviewsHeaders: [
        { text: "Job ID", value: "jobId", width: "70px", sortable: true },
        { text: "Hyg ID", value: "hygienistId", width: "70px", sortable: true },
        { text: "Hygienist", value: "hygienistName", width: "140px", sortable: false },
        { text: "Friendliness", value: "friendlinessRating", width: "95px", sortable: false },
        { text: "Friendliness Comments", value: "friendlinessComments", sortable: false },
        { text: "Infection Ctrl", value: "infectionControlRating", width: "100px", sortable: false },
        { text: "Infection Ctrl Comments", value: "infectionControlComments", sortable: false },
        { text: "Equipment", value: "equipmentRating", width: "90px", sortable: false },
        { text: "Equipment Comments", value: "equipmentComments", sortable: false },
        { text: "Preparation", value: "preparationRating", width: "95px", sortable: false },
        { text: "Preparation Comments", value: "preparationComments", sortable: false },
        { text: "Collaboration", value: "collaborationPreference", width: "105px", sortable: false },
        { text: "Additional Feedback", value: "additionalFeedback", sortable: false },
        { text: "Review Date", value: "reviewDate", width: "110px", sortable: true }
      ]
    };
  },
  computed: {
    locationCount(): number {
      return (this as any).totalLocationCount;
    }
  },
  async created() {
    // Set tablename for Location component
    this.$store.commit("modify", { property: "tablename", value: "Location" });
    // Fetch total count
    await this.fetchTotalLocationCount();
  },
  watch: {
    "$store.state.tableContent": {
      handler() {
        // Refresh total count when table content changes (pagination, search, etc.)
        this.fetchTotalLocationCount();
      },
      deep: true
    }
  },
  methods: {
    resetCollapsedSections() {
      Object.keys(this.collapsedSections).forEach(section => {
        this.$set(this.collapsedSections, section, true);
      });
    },
    toggleSection(section: string) {
      this.$set(
        this.collapsedSections,
        section,
        nextLocationSectionState(this.collapsedSections, section)
      );
    },
    // Fetch total location count
    async fetchTotalLocationCount() {
      try {
        this.totalLocationCount = await totalItemsByTable({
          tableName: "Location",
          columnNames: constants.enabledColumns.locationTable
        });
      } catch (error) {
        // Error fetching total location count
        this.totalLocationCount = 0;
      }
    },
    // Handle location selection from Location component
    async handleLocationSelected(location: any) {
      this.cancelAllRequests();
      this.selectedLocation = location;
      this.resetCollapsedSections();
      this.resetOfficeDocumentUpload();
      this.cancelEditNotificationEmail();
      this.cancelEditLoginEmail();

      // Reset pagination to page 1 for new location
      this.invoicesOptions = { page: 1, itemsPerPage: 5, sortBy: ["id"], sortDesc: [true] };
      this.paymentsOptions = { page: 1, itemsPerPage: 5, sortBy: ["id"], sortDesc: [true] };
      this.cancellationsOptions = { page: 1, itemsPerPage: 10, sortBy: ["cancelTimestamp"], sortDesc: [true] };
      this.cancellationsData = [];
      this.cancellationsTotal = 0;

      // Fetch all related data in parallel
      await this.fetchLocationDetails();
    },

    // Clear selection
    clearSelection() {
      this.selectedLocation = null;
      this.resetCollapsedSections();
      this.associatedLocations = [];
      this.associatedLocationsError = "";
      this.resetOfficeDocumentUpload();
      this.cancelEditNotificationEmail();
      this.cancelEditLoginEmail();
      this.cancelAllRequests();
      if (this.talentReviewsAbortController) {
        this.talentReviewsAbortController.abort();
        this.talentReviewsAbortController = null;
      }
      this.invoicesData = [];
      this.paymentsData = [];
      this.placementFeesData = [];
      this.placementFeesTotal = 0;
      this.cancellationsData = [];
      this.cancellationsTotal = 0;
      this.notesData = [];
      this.feedbackData = [];
      this.invoicesTotal = 0;
      this.paymentsTotal = 0;
      this.talentReviewsData = [];
      this.talentReviewsTotal = 0;
      this.invoicesOptions = { page: 1, itemsPerPage: 5, sortBy: ["id"], sortDesc: [true] };
      this.paymentsOptions = { page: 1, itemsPerPage: 5, sortBy: ["id"], sortDesc: [true] };
      this.talentReviewsOptions = { page: 1, itemsPerPage: 5, sortBy: ["reviewDate"], sortDesc: [true] };
      this.$store.commit("modify", {
        property: "selectedLocationId",
        value: null
      });
    },

    // Fetch all location details
    async fetchLocationDetails() {
      if (!this.selectedLocation) return;

      const locationId = this.selectedLocation.id;

      // Fetch all data in parallel (jobs now live in the PostedJobs component)
      await Promise.all([
        this.fetchInvoices(locationId),
        this.fetchPayments(locationId),
        this.fetchPlacementFees(locationId),
        this.fetchCancellations(locationId),
        this.fetchNotes(locationId),
        this.fetchFeedback(locationId),
        this.fetchAssociatedLocations(locationId)
      ]);
    },

    cancelSectionRequest(section: "jobs" | "invoices" | "payments" | "cancellations") {
      if (this.abortControllers[section]) {
        this.abortControllers[section]!.abort();
      }
      this.abortControllers[section] = new AbortController();
      return this.abortControllers[section]!.signal;
    },

    cancelAllRequests() {
      (["jobs", "invoices", "payments", "cancellations"] as const).forEach(section => {
        if (this.abortControllers[section]) {
          this.abortControllers[section]!.abort();
          this.abortControllers[section] = null;
        }
      });
    },

    async fetchAssociatedLocations(locationId: number) {
      this.loading.associatedLocations = true;
      this.associatedLocationsError = "";
      try {
        const result = await getLocationSiblings(locationId);
        this.associatedLocations = result.data || [];
      } catch (error) {
        this.associatedLocations = [];
        const message =
          error instanceof Error ? error.message : "Request failed";
        this.associatedLocationsError = message;
        this.$store.commit("callSnackbar", {
          text: `Associated locations failed to load: ${message}`,
          color: "#FB7672"
        });
      } finally {
        this.loading.associatedLocations = false;
      }
    },

    async fetchInvoices(locationId: number) {
      const signal = this.cancelSectionRequest("invoices");
      this.loading.invoices = true;
      try {
        const opts = this.invoicesOptions;
        const sortBy = opts.sortBy && opts.sortBy.length ? opts.sortBy[0] : "id";
        const sortDesc = opts.sortDesc && opts.sortDesc.length ? opts.sortDesc[0] : true;
        const result = await getLocationInvoicesPaginated(
          locationId, opts.page || 1, opts.itemsPerPage || 5, sortBy, sortDesc, signal
        );
        this.invoicesData = result.data;
        this.invoicesTotal = result.total;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
        this.invoicesData = [];
        this.invoicesTotal = 0;
      } finally {
        this.loading.invoices = false;
      }
    },

    // Office-paid remains editable; Talent-paid is lifecycle-owned and read-only.
    async updateOfficePaidStatus(item: any, value: number) {
      try {
        await updateInvoice(buildOfficePaidUpdate(item.id, value));
        // Optimistic local update so the chip reflects the new value immediately.
        this.invoicesData = this.invoicesData.map((invoice: any) =>
          invoice.id === item.id
            ? { ...invoice, dentistMarkedPaid: value }
            : invoice
        );
        this.$store.commit("callSnackbar", {
          text: "Office paid status updated.",
          color: "#13CA82"
        });
      } catch (error) {
        this.$store.commit("callSnackbar", {
          text:
            (error instanceof Error && error.message) ||
            "Could not update the paid status.",
          color: "#FB7672"
        });
        // Refetch to revert the optimistic edit back to server truth.
        if (this.selectedLocation && this.selectedLocation.id) {
          this.fetchInvoices(this.selectedLocation.id);
        }
      }
    },

    async fetchPayments(locationId: number) {
      const signal = this.cancelSectionRequest("payments");
      this.loading.payments = true;
      try {
        const opts = this.paymentsOptions;
        const sortBy = opts.sortBy && opts.sortBy.length ? opts.sortBy[0] : "id";
        const sortDesc = opts.sortDesc && opts.sortDesc.length ? opts.sortDesc[0] : true;
        const result = await getLocationPaymentsPaginated(
          locationId, opts.page || 1, opts.itemsPerPage || 5, sortBy, sortDesc, signal
        );
        this.paymentsData = result.data;
        this.paymentsTotal = result.total;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
        this.paymentsData = [];
        this.paymentsTotal = 0;
      } finally {
        this.loading.payments = false;
      }
    },


    onInvoicesOptionsChange() {
      if (this.selectedLocation) {
        this.fetchInvoices(this.selectedLocation.id);
      }
    },

    // T2-6221: Placement Fee history (read-only, newest first).
    async fetchPlacementFees(locationId: number) {
      this.loading.placementFees = true;
      try {
        const opts = this.placementFeesOptions;
        const result = await getLocationPlacementFeesPaginated(
          locationId,
          opts.page || 1,
          opts.itemsPerPage || 10
        );
        this.placementFeesData = result.data;
        this.placementFeesTotal = result.total;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
        this.placementFeesData = [];
        this.placementFeesTotal = 0;
      } finally {
        this.loading.placementFees = false;
      }
    },

    onPlacementFeesOptionsChange() {
      if (this.selectedLocation) {
        this.fetchPlacementFees(this.selectedLocation.id);
      }
    },

    // T2-6704: Office cancellation overview
    hydrateCancellationRows(rows: any[]) {
      return (rows || []).map((row: any) => ({
        ...row,
        editReason: row.reason || "",
        editDetails: row.details || "",
        originalReason: row.reason || "",
        originalDetails: row.details || "",
        saving: false
      }));
    },

    officeCancellationReasonOptions(item: any) {
      const current = String(item.editReason || "").trim();
      if (current && !OFFICE_CANCELLATION_REASON_CODES.has(current)) {
        return [
          { value: current, text: `${current} (historical)` },
          ...OFFICE_CANCELLATION_REASON_OPTIONS
        ];
      }
      return OFFICE_CANCELLATION_REASON_OPTIONS;
    },

    isCancellationRowDirty(item: any) {
      return (
        String(item.editReason || "") !== String(item.originalReason || "") ||
        String(item.editDetails || "") !== String(item.originalDetails || "")
      );
    },

    talentLabel(item: any) {
      const name = [item.firstName, item.lastName].filter(Boolean).join(" ").trim();
      return name || String(item.hygienistId);
    },

    formatDateTime(dateString: string) {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleString();
      } catch {
        return dateString;
      }
    },

    async fetchCancellations(locationId: number) {
      const signal = this.cancelSectionRequest("cancellations");
      this.loading.cancellations = true;
      try {
        const opts = this.cancellationsOptions;
        const sortBy =
          opts.sortBy && opts.sortBy.length ? opts.sortBy[0] : "cancelTimestamp";
        const sortDesc =
          opts.sortDesc && opts.sortDesc.length ? opts.sortDesc[0] : true;
        const result = await getLocationCancellationsPaginated(
          locationId,
          opts.page || 1,
          opts.itemsPerPage || 10,
          sortBy,
          sortDesc,
          signal
        );
        this.cancellationsData = this.hydrateCancellationRows(result.data || []);
        this.cancellationsTotal = result.total || 0;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
        this.cancellationsData = [];
        this.cancellationsTotal = 0;
      } finally {
        this.loading.cancellations = false;
      }
    },

    onCancellationsOptionsChange() {
      if (this.selectedLocation) {
        this.fetchCancellations(this.selectedLocation.id);
      }
    },

    async saveLocationCancellation(item: any) {
      if (!this.selectedLocation || !item) return;
      item.saving = true;
      try {
        const result = await updateLocationCancellation(
          this.selectedLocation.id,
          item.id,
          {
            reason: item.editReason,
            details: item.editDetails
          }
        );
        const updated = result && result.cancellation ? result.cancellation : null;
        item.reason = updated ? updated.reason : item.editReason;
        item.details = updated ? updated.details : item.editDetails;
        item.originalReason = item.reason || "";
        item.originalDetails = item.details || "";
        item.editReason = item.originalReason;
        item.editDetails = item.originalDetails;
        this.$store.commit("callSnackbar", {
          text: result && result.metadataOnly
            ? "Cancellation metadata saved"
            : "Cancellation saved and Talent attribution updated",
          color: "#13CA82"
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to save cancellation";
        this.$store.commit("callSnackbar", {
          text: message,
          color: "#FB7672"
        });
      } finally {
        item.saving = false;
      }
    },

    // T2-6866: inline edit of Location notification email (writes to Location.email only).
    // Uses the same blank/null + format rules as Location table UpdateMultipleItems for `email`.
    startEditNotificationEmail() {
      this.notificationEmailDraft = this.selectedLocation?.email || "";
      this.notificationEmailError = "";
      this.editingNotificationEmail = true;
    },
    cancelEditNotificationEmail() {
      this.editingNotificationEmail = false;
      this.notificationEmailError = "";
    },
    async saveNotificationEmail() {
      if (!this.selectedLocation || this.savingNotificationEmail) return;
      const email = (this.notificationEmailDraft || "").trim();
      // Match UpdateMultipleItems: blank allowed; otherwise must look like an email.
      if (email && !/.+@.+\..+/.test(email)) {
        this.notificationEmailError = "E-mail must be valid";
        return;
      }
      const prevValue = this.selectedLocation.email || null;
      const value = email || null;
      this.savingNotificationEmail = true;
      this.notificationEmailError = "";
      try {
        await updateTable({
          tablename: "Location",
          columnname: "email",
          id: this.selectedLocation.id,
          prevValue,
          value
        });
        this.selectedLocation.email = value;
        this.editingNotificationEmail = false;
        this.$store.commit("callSnackbar", {
          text: "Notification email updated.",
          color: "#13CA82"
        });
      } catch (error) {
        this.notificationEmailError =
          (error instanceof Error && error.message) ||
          "Could not update the notification email.";
      } finally {
        this.savingNotificationEmail = false;
      }
    },

    // T2-6223: inline edit of the Location login email (writes to User.email).
    startEditLoginEmail() {
      this.loginEmailDraft = this.selectedLocation?.loginEmail || "";
      this.loginEmailError = "";
      this.editingLoginEmail = true;
    },
    cancelEditLoginEmail() {
      this.editingLoginEmail = false;
      this.loginEmailError = "";
    },
    async saveLoginEmail() {
      if (!this.selectedLocation) return;
      const email = (this.loginEmailDraft || "").trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.loginEmailError = "Please enter a valid email address.";
        return;
      }
      this.savingLoginEmail = true;
      this.loginEmailError = "";
      try {
        await updateLocationLoginEmail(this.selectedLocation.id, email);
        this.selectedLocation.loginEmail = email;
        this.editingLoginEmail = false;
        this.$store.commit("callSnackbar", {
          text: "Login email updated.",
          color: "#13CA82"
        });
      } catch (error) {
        this.loginEmailError =
          (error instanceof Error && error.message) ||
          "Could not update the login email.";
      } finally {
        this.savingLoginEmail = false;
      }
    },

    onPaymentsOptionsChange() {
      if (this.selectedLocation) {
        this.fetchPayments(this.selectedLocation.id);
      }
    },

    async fetchNotes(locationId: number) {
      this.loading.notes = true;
      try {
        this.notesData = await getNotesByLocation(locationId);
      } catch (error) {
        this.notesData = [];
      } finally {
        this.loading.notes = false;
      }
    },

    async fetchFeedback(locationId: number) {
      this.loading.feedback = true;
      try {
        this.feedbackData = await getFeedbackByLocation(locationId);
      } catch (error) {
        this.feedbackData = [];
      } finally {
        this.loading.feedback = false;
      }
    },

    // Admin Actions
    handleBlockTalent() {
      if (!this.hygienistIdToBlock) {
        this.$store.commit("callSnackbar", {
          text: "Please enter a Hygienist ID",
          color: "#FB7672"
        });
        return;
      }
      this.blockTalentDialog = true;
    },

    async confirmBlockTalent() {
      if (!this.selectedLocation || !this.hygienistIdToBlock) return;

      this.loading.blockTalent = true;
      try {
        await blockTalentFromLocation(
          this.selectedLocation.id,
          this.hygienistIdToBlock
        );

        this.$store.commit("callSnackbar", {
          text: `Hygienist ${this.hygienistIdToBlock} blocked from ${this.selectedLocation.practiceName}`,
          color: "#13CA82"
        });

        this.blockTalentDialog = false;
        this.hygienistIdToBlock = null;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Error blocking talent";
        this.$store.commit("callSnackbar", {
          text: errorMessage,
          color: "#FB7672"
        });
      } finally {
        this.loading.blockTalent = false;
      }
    },

    handleReactivate() {
      this.reactivateDialog = true;
    },

    async confirmReactivate() {
      // The dialog can be closed and another row selected (or the selection
      // cleared) while the request runs, so keep the location it is for.
      const targetLocation = this.selectedLocation;
      if (!targetLocation) return;

      this.loading.reactivate = true;
      try {
        await reactivateLocation(targetLocation.id, targetLocation.deactivated);

        // App.vue's v-snackbar only opens when `visible` is true.
        this.$store.commit("callSnackbar", {
          visible: true,
          text: `${targetLocation.practiceName} reactivated successfully`,
          color: "#13CA82"
        });

        // Update local data
        targetLocation.deactivated = 0;
        this.reactivateDialog = false;

        // Refresh the Locations list so this row's Deleted chip turns Active
        await (this.$refs.locationTable as any)?.getDataFromApi();
        // Refresh total count
        await this.fetchTotalLocationCount();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Error reactivating location";
        this.$store.commit("callSnackbar", {
          visible: true,
          text: errorMessage,
          color: "#FB7672"
        });
      } finally {
        this.loading.reactivate = false;
      }
    },

    // T2-5692: reactivate the deactivated User account behind a dental office.
    // This is separate from `confirmReactivate`, which only flips Location.deactivated.
    handleReactivateAccount() {
      this.reactivateAccountDialog = true;
    },

    async confirmReactivateAccount() {
      if (!this.selectedLocation || !this.selectedLocation.dentistId) return;

      this.loading.reactivateAccount = true;
      try {
        const { adminReactivateUser } = await import("@/api");
        const result = await adminReactivateUser({
          dentistId: this.selectedLocation.dentistId,
        });

        this.$store.commit("callSnackbar", {
          text:
            `Account for ${this.selectedLocation.practiceName} reactivated. ` +
            `Mailchimp: ${result.mailChimpSubscribed ? "subscribed" : "skipped"}`,
          color: "#13CA82",
        });

        // Reflect the new status in-place and refresh the backing table data.
        this.selectedLocation.accountStatus = 1;
        this.reactivateAccountDialog = false;

        await this.$store.dispatch("getData", { tablename: "Location" });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Error reactivating office account";
        this.$store.commit("callSnackbar", {
          text: errorMessage,
          color: "#FB7672",
        });
      } finally {
        this.loading.reactivateAccount = false;
      }
    },

    viewDocument() {
      if (this.selectedLocation?.officeDocument) {
        window.open(this.selectedLocation.officeDocument, "_blank");
      }
    },

    resetOfficeDocumentUpload() {
      this.officeDocumentFile = null;
      this.officeDocumentError = "";
      this.resetOfficeDocumentInput();
    },

    resetOfficeDocumentInput() {
      const refs = this.$refs as {
        officeDocumentInput?: {
          reset: () => void;
        };
      };
      if (
        refs.officeDocumentInput &&
        typeof refs.officeDocumentInput.reset === "function"
      ) {
        refs.officeDocumentInput.reset();
      }
    },

    onOfficeDocumentChange(): void {
      this.officeDocumentError = "";
      if (!this.officeDocumentFile) {
        return;
      }

      const isPdf =
        this.officeDocumentFile.type === "application/pdf" ||
        this.officeDocumentFile.name.toLowerCase().endsWith(".pdf");

      if (!isPdf) {
        this.officeDocumentError =
          "Only PDF files are allowed. Please select a PDF file.";
        this.officeDocumentFile = null;
        this.resetOfficeDocumentInput();
      }
    },

    async uploadOfficeDocument(): Promise<void> {
      if (!this.selectedLocation || !this.officeDocumentFile) {
        return;
      }

      if (this.officeDocumentError) {
        return;
      }

      this.loading.officeDocumentUpload = true;
      try {
        const documentUrl = await uploadOfficeDocumentToS3(
          this.officeDocumentFile
        );
        await updateTable({
          tablename: "Location",
          columnname: "photoUrl",
          id: this.selectedLocation.id,
          value: documentUrl,
          prevValue: this.selectedLocation.officeDocument || null
        });

        this.selectedLocation.officeDocument = documentUrl;
        await this.$store.dispatch("getData", { tablename: "Location" });

        this.$store.commit("callSnackbar", {
          text: "Office document uploaded successfully",
          color: "#13CA82"
        });

        this.resetOfficeDocumentUpload();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Error uploading office document";
        this.$store.commit("callSnackbar", {
          text: errorMessage,
          color: "#FB7672"
        });
      } finally {
        this.loading.officeDocumentUpload = false;
      }
    },

    // Admin Notes Methods
    openAddNoteDialog() {
      this.newNote = "";
      this.addNoteDialog = true;
    },

    closeAddNoteDialog() {
      this.addNoteDialog = false;
      this.newNote = "";
    },

    async saveAdminNote() {
      if (!this.selectedLocation || !this.newNote || !this.newNote.trim()) {
        return;
      }

      this.loading.addNote = true;
      try {
        // Get current user info for source
        const userString = localStorage.getItem("user") || "";
        let agent = "Admin";
        if (userString) {
          try {
            const userObj = userString.startsWith("{")
              ? JSON.parse(userString)
              : userString;
            agent = typeof userObj === "string" ? userObj : userObj.username || userObj.email || "Admin";
          } catch {
            agent = userString;
          }
        }

        await createNote({
          entityId: this.selectedLocation.id,
          type: "Location",
          note: this.newNote.trim(),
          source: agent
        });

        // Refresh notes
        await this.fetchNotes(this.selectedLocation.id);

        this.$store.commit("callSnackbar", {
          text: "Admin note added successfully",
          color: "#13CA82"
        });

        this.closeAddNoteDialog();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Error adding admin note";
        this.$store.commit("callSnackbar", {
          text: errorMessage,
          color: "#FB7672"
        });
      } finally {
        this.loading.addNote = false;
      }
    },

    // Utility methods
    /**
     * Returns the descriptive label for a given account status value.
     */
    getAccountStatusLabel(status: number | null | undefined) {
      if (status === null || typeof status === "undefined") {
        return "UNKNOWN";
      }
      const label = constants.userAccountStatuses[status];
      if (label) {
        return label;
      }
      return `STATUS ${status}`;
    },

    formatDate(dateString: string) {
      if (!dateString) return "N/A";
      try {
        const str = String(dateString).trim();
        const dateMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
        if (dateMatch) {
          return format(new Date(dateMatch[1] + "T00:00:00"), "MMM d, yyyy");
        }
        return format(new Date(str), "MMM d, yyyy");
      } catch {
        return dateString;
      }
    },

    isAccountDeleted(status: number | null | undefined): boolean {
      if (status === null || typeof status === "undefined") {
        return false;
      }
      return Number(status) === -4;
    },

    getCancellationColor(percentage: number | null | undefined): string {
      if (percentage === null || percentage === undefined) {
        return "grey";
      }
      if (percentage <= 5) {
        return "#4CAF50"; // Green - excellent
      }
      if (percentage <= 10) {
        return "#8BC34A"; // Light green - good
      }
      if (percentage <= 15) {
        return "#FF9800"; // Orange - warning
      }
      if (percentage <= 25) {
        return "#FF5722"; // Deep orange - concerning
      }
      return "#F44336"; // Red - high cancellation rate
    },

    getNonPaymentColor(percentage: number | null | undefined): string {
      if (percentage === null || percentage === undefined) {
        return "grey";
      }
      if (percentage <= 1) {
        return "#4CAF50"; // Green - excellent
      }
      if (percentage <= 5) {
        return "#FF9800"; // Orange - warning
      }
      return "#F44336"; // Red - concerning
    },

    async handleShowStats() {
      if (!this.selectedLocation) {
        this.$store.commit("callSnackbar", {
          text: "Please select a location first",
          color: "#FB7672"
        });
        return;
      }

      this.loadingStats = true;
      this.showStatsDialog = true;
      this.locationStats = null;

      try {
        const stats = await getLocationStats(this.selectedLocation.id);
        this.locationStats = stats;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Error fetching stats";
        this.$store.commit("callSnackbar", {
          text: message,
          color: "#FB7672"
        });
      } finally {
        this.loadingStats = false;
      }
    },

    async handleShowTalentReviews() {
      if (!this.selectedLocation) {
        this.$store.commit("callSnackbar", {
          text: "Please select a location first",
          color: "#FB7672"
        });
        return;
      }
      this.showTalentReviewsDialog = true;
      this.talentReviewsSearch = "";
      this.talentReviewsOptions = { page: 1, itemsPerPage: 5, sortBy: ["reviewDate"], sortDesc: [true] };
      await this.fetchTalentReviews(this.selectedLocation.id);
    },

    async fetchTalentReviews(locationId: number) {
      if (this.talentReviewsAbortController) {
        this.talentReviewsAbortController.abort();
      }
      this.talentReviewsAbortController = new AbortController();
      const signal = this.talentReviewsAbortController.signal;
      this.loadingTalentReviewsTable = true;
      try {
        const opts = this.talentReviewsOptions;
        const sortBy = opts.sortBy && opts.sortBy.length ? opts.sortBy[0] : "reviewDate";
        const sortDesc = opts.sortDesc && opts.sortDesc.length ? opts.sortDesc[0] : true;
        const result = await getLocationTalentReviewsPaginated(
          locationId,
          opts.page || 1,
          opts.itemsPerPage || 5,
          sortBy,
          sortDesc,
          this.talentReviewsSearch || "",
          signal
        );
        this.talentReviewsData = result.data;
        this.talentReviewsTotal = result.total;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
        this.talentReviewsData = [];
        this.talentReviewsTotal = 0;
        const message = error instanceof Error ? error.message : "Error fetching feedback";
        this.$store.commit("callSnackbar", { text: message, color: "#FB7672" });
      } finally {
        this.loadingTalentReviewsTable = false;
      }
    },

    onTalentReviewsOptionsChange() {
      if (this.selectedLocation && this.showTalentReviewsDialog) {
        this.fetchTalentReviews(this.selectedLocation.id);
      }
    },

    onTalentReviewsSearch() {
      this.talentReviewsOptions = { ...this.talentReviewsOptions, page: 1 };
      if (this.selectedLocation) {
        this.fetchTalentReviews(this.selectedLocation.id);
      }
    },

    onTalentReviewsClearSearch() {
      this.talentReviewsSearch = "";
      this.talentReviewsOptions = { ...this.talentReviewsOptions, page: 1 };
      if (this.selectedLocation) {
        this.fetchTalentReviews(this.selectedLocation.id);
      }
    },

    getCollaborationPreferenceText(val: number | null): string {
      if (val === 1) return "Love";
      if (val === 2) return "Fine";
      if (val === 3) return "Prefer Not";
      return "N/A";
    },

    getCollaborationPreferenceColor(val: number | null): string {
      if (val === 1) return "#13CA82";
      if (val === 2) return "#0B4598";
      if (val === 3) return "#FB7672";
      return "grey";
    },

    openFeedbackTextDialog(text: string) {
      this.feedbackTextDialogContent = text;
      this.feedbackTextDialog = true;
    }
  }
});
